import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import type {
	IssueId,
	IssueKey,
	IssueTypeId,
	ProjectId,
	ProjectKey,
	SprintId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import type { BoardType } from '@atlassian/jira-shared-types/src/rest/jira/board.tsx';
import type {
	ACTIVE_SPRINT_STATE,
	FUTURE_SPRINT_STATE,
} from '@atlassian/jira-shared-types/src/rest/jira/sprint.tsx';
import type { ForgeCustomFieldValue } from './contexts/custom-field.tsx';
import type {
	AdminPageModule,
	CustomFieldModule,
	CustomFieldTypeModule,
	DashboardBackgroundScriptModule,
	DashboardGadgetModule,
	EditEntryPoint,
	EntryPoint,
	ExtensionPointModule,
	GlobalPageModule,
	IssueActionModule,
	IssueActivityModule,
	IssueGlanceModule,
	IssueContextModule,
	IssuePanelModule,
	IssueNavigatorActionModule,
	ProjectPageModule,
	ProjectSettingsPageModule,
	ServiceDeskOrganizationPanelModule,
	ServiceDeskQueuePageModule,
	WorkflowValidatorModule,
	WorkflowConditionModule,
	WorkflowPostFunctionModule,
	IssueViewBackgroundScriptModule,
	ServiceDeskAssetsImportTypeModule,
	BacklogActionModule,
	BoardActionModule,
	SprintActionModule,
	PersonalSettingsPageModule,
	GlobalBackgroundScriptModule,
} from './module.tsx';
import type { CustomFieldEditRenderContext, CustomFieldViewRenderContext } from './extension.tsx';

// Legacy
export type LegacyForgeUiIssueContext = {
	issueKey: IssueKey;
	issueId?: IssueId;
	issueType: string;
	issueTypeId: string;
	projectKey: ProjectKey;
	projectId: ProjectId | null;
};

export type LegacyAdminPageExtensionContext = {
	type: 'adminPage';
};
export type LegacyIssueActionExtensionContext = {
	type: 'issueAction';
};
export type LegacyIssueActivityExtensionContext = {
	type: 'issueActivity';
};
export type LegacyIssueGlanceExtensionContext = {
	type: 'issueGlance';
};
export type LegacyIssuePanelExtensionContext = {
	type: 'issuePanel';
	isNewToIssue: boolean;
};
export type LegacyCustomFieldExtensionContext = {
	type: 'customField';
	fieldValue: ForgeCustomFieldValue;
};

export type LegacyExtensionContext =
	| LegacyIssueActionExtensionContext
	| LegacyIssueActivityExtensionContext
	| LegacyIssueGlanceExtensionContext
	| LegacyIssuePanelExtensionContext
	| LegacyCustomFieldExtensionContext
	| LegacyAdminPageExtensionContext;

export type LegacyIframeContext = {
	localId?: string;
} & LegacyExtensionContext &
	LegacyForgeUiIssueContext;

// New context
export type GenericExtensionData = {
	type: ExtensionPointModule;
};

export type ForgeUiBoardData = {
	board: {
		id: string;
		type: BoardType;
	};
	project: {
		id: ProjectId;
		key: ProjectKey;
		type: ProjectType;
	};
};

export type ForgeUiIssueData = {
	issue: {
		key?: IssueKey; // in create-issue it is not defined,
		id?: IssueId;
		type: string;
		typeId: IssueTypeId;
	};
	project: {
		key: ProjectKey;
		id: ProjectId | null;
		type: ProjectType | null;
	};
};

export type ForgeUiIssueCreateData = {
	project: {
		key: ProjectKey;
		type: ProjectType;
		id: ProjectId;
	};
	issue: {
		type: string;
		typeId: IssueTypeId;
	};
};

export type ForgeUiIssueTransitionData = {
	project: {
		key: ProjectKey;
		type: ProjectType;
		id: ProjectId;
	};
	issueTransition: {
		id: string;
	};
	issue: {
		id: IssueId;
		key: IssueKey;
		type: string;
		typeId: IssueTypeId;
	};
};

export type ForgeUiProjectData = {
	project: {
		key: ProjectKey;
		type: ProjectType | null;
		id: ProjectId | null;
	};
};

export type ForgeUiIssueNavigatorData = {
	jql: string | null;
	filterId?: string;
	issueKeys: string[];
	action?: string;
};

// Board related extension data types
type BoardExtensionData<M> = {
	type: M;
} & ForgeUiBoardData;

export type SprintState = typeof FUTURE_SPRINT_STATE | typeof ACTIVE_SPRINT_STATE;

export type SprintExtensionData = {
	id: SprintId;
	state?: Lowercase<SprintState>;
};

export type BacklogActionExtensionData = BoardExtensionData<BacklogActionModule>;

export type BoardActionExtensionData = BoardExtensionData<BoardActionModule> & {
	sprints: SprintExtensionData[] | undefined;
};

export type SprintActionExtensionData = BoardExtensionData<SprintActionModule> & {
	sprint: SprintExtensionData;
};

export type GenericIssueExtensionData = GenericExtensionData & ForgeUiIssueData;

export const isGenericIssueExtensionData = (
	extensionData: GenericExtensionData,
): extensionData is GenericIssueExtensionData =>
	'issue' in extensionData && 'project' in extensionData;

type IssueExtensionData<M> = {
	type: M;
} & ForgeUiIssueData;

export type AdminPageExtensionData = {
	type: AdminPageModule;
};
export type ProjectPageExtensionData = ForgeUiProjectData & {
	// board optional since JSM project pages aren't rendered within the context of a board
	board?: ForgeUiBoardData['board'];
	type: ProjectPageModule;
};
export type ProjectSettingsPageExtensionData = ForgeUiProjectData & {
	type: ProjectSettingsPageModule;
};
export type GlobalPageExtensionData = {
	type: GlobalPageModule;
};
export type PersonalSettingsPageExtensionData = {
	type: PersonalSettingsPageModule;
};
export type IssueNavigatorActionData<M> = {
	type: M;
} & ForgeUiIssueNavigatorData;
export type IssueNavigatorActionExtensionData =
	IssueNavigatorActionData<IssueNavigatorActionModule>;

export type IssueActionExtensionData = IssueExtensionData<IssueActionModule>;
export type IssueActivityExtensionData = IssueExtensionData<IssueActivityModule>;
export type IssueGlanceExtensionData = IssueExtensionData<IssueGlanceModule>;
export type IssueContextExtensionData = IssueExtensionData<IssueContextModule>;
export type IssuePanelExtensionData = IssueExtensionData<IssuePanelModule> & {
	isNewToIssue: boolean;
};
export type IssueViewBackgroundScriptExtensionData = Omit<
	IssueExtensionData<IssueViewBackgroundScriptModule>,
	'issue'
> & {
	issue: Omit<IssueExtensionData<IssueViewBackgroundScriptModule>['issue'], 'type' | 'typeId'>;
};

export type CustomFieldExtensionGenericData = {
	fieldId: string;
	fieldName: string;
	fieldType: string;
	renderContext: string;
	entryPoint?: EntryPoint;
	isInline?: boolean;
	experience?: CustomFieldViewRenderContext | CustomFieldEditRenderContext; // remove optional once ditto-fcf-support-new-manifest-on-frontend is cleaned up
};

export type CustomFieldExtensionData = Pick<
	IssueExtensionData<CustomFieldModule | CustomFieldTypeModule>,
	'type'
> &
	Partial<IssueExtensionData<CustomFieldModule | CustomFieldTypeModule>> &
	CustomFieldExtensionGenericData;

export type ForgeUiWorkflowValidatorConfigData = {
	validatorId: string;
	validatorConfig?: string;
	workflowName?: string;
};

export type WorkflowValidatorExtensionData<EP> = ForgeUiWorkflowValidatorConfigData & {
	type: WorkflowValidatorModule;
	entryPoint: EP;
};

export type ForgeUiWorkflowConditionConfigData = {
	conditionId: string;
	conditionConfig?: string;
	workflowName?: string;
};

export type WorkflowConditionExtensionData<EP> = ForgeUiWorkflowConditionConfigData & {
	type: WorkflowConditionModule;
	entryPoint: EP;
};

export type ForgeUiWorkflowPostFunctionConfigData = {
	postFunctionId: string;
	postFunctionConfig?: string;
	workflowName?: string;
};

export type WorkflowPostFunctionExtensionData<EP> = ForgeUiWorkflowPostFunctionConfigData & {
	type: WorkflowPostFunctionModule;
	entryPoint: EP;
};

export type CustomFieldContextConfigExtensionData = {
	type: CustomFieldTypeModule;
	fieldId: string;
	fieldType: string;
	configurationId: number;
	fieldContextId: string;
	configuration: unknown;
	entryPoint?: EntryPoint;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	schema: Record<any, any> | undefined;
};

export type ForgeUiGadgetConfigData = Record<string, string | boolean | number>;

export type DashboardBackgroundScriptExtensionData = {
	type: DashboardBackgroundScriptModule;
};

export type GlobalBackgroundScriptExtensionData = {
	type: GlobalBackgroundScriptModule;
};

export type DashboardGadgetExtensionData = {
	type: DashboardGadgetModule;
	gadgetConfiguration: ForgeUiGadgetConfigData;
	entryPoint?: EditEntryPoint;
	dashboard: {
		id: string;
	};
	gadget: {
		id: string;
	};
};

export type ForgeUiServiceDeskQueueData = {
	project: {
		key: ProjectKey;
		id: ProjectId | null;
	};
};

export type ServiceDeskQueuePageExtensionData = ForgeUiServiceDeskQueueData & {
	type: ServiceDeskQueuePageModule;
};

export type ForgeUiServiceDeskOrganizationPanelData = {
	project: {
		key: ProjectKey;
		id: ProjectId | null;
	};
	organization: {
		id: number;
	};
};

export type ServiceDeskOrganizationPanelExtensionData = ForgeUiServiceDeskOrganizationPanelData & {
	type: ServiceDeskOrganizationPanelModule;
};

export type ServiceDeskAssetsImportTypeExtensionData = {
	type: ServiceDeskAssetsImportTypeModule;
	importId: string;
	workspaceId: string;
	schemaId: string;
};

export type ExtensionData =
	| BacklogActionExtensionData
	| BoardActionExtensionData
	| SprintActionExtensionData
	| IssueActionExtensionData
	| IssueActivityExtensionData
	| IssueGlanceExtensionData
	| IssueContextExtensionData
	| IssuePanelExtensionData
	| IssueViewBackgroundScriptExtensionData
	| CustomFieldExtensionData
	| CustomFieldContextConfigExtensionData
	| AdminPageExtensionData
	| ProjectPageExtensionData
	| ProjectSettingsPageExtensionData
	| GlobalPageExtensionData
	| DashboardBackgroundScriptExtensionData
	| DashboardGadgetExtensionData
	| ServiceDeskQueuePageExtensionData
	| ServiceDeskOrganizationPanelExtensionData
	| ServiceDeskAssetsImportTypeExtensionData
	| GlobalBackgroundScriptExtensionData;
